import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { primaryColor, secondaryColor } from '../../../../utils/comon';
import PageHeadingSection from '../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../services/apiService';
import Breadcrumbs from '../../../common/BreadCrum/BreadCrum';
import CustomSelect from '../../../common/formfields/CustomSelect';

const AlertNotificationForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    // In React Router v6, id could be in useParams or location.state
    const editId = id || location.state?.id;
    const isEdit = Boolean(editId);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        status: "Active",
        imageSrc: null
    });

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isEdit) {
            fetchAlertData();
        }
    }, [isEdit, editId]);

    const fetchAlertData = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/RaisedAlert/GetRaisedAlertById/${editId}`
            });

            if (response.isSuccess || response.success || response.IsSuccess) {
                const apiData = response.data;
                const data = Array.isArray(apiData) ? apiData[0] : apiData;

                if (data) {
                    const activeStatus = data.IsActive || data.isActive;
                    let parsedStatus = "Active";
                    if (activeStatus === "Inactive" || activeStatus === 0) parsedStatus = "Inactive";
                    if (activeStatus === "Active" || activeStatus === 1) parsedStatus = "Active";

                    setFormData({
                        title: data.AlertTypeName || data.alertTypeName || "",
                        description: data.AlertDescription || data.alertDescription || "",
                        status: parsedStatus,
                        imageSrc: data.AlertImage || data.alertImage || null
                    });
                }
            } else {
                toast.error("Failed to load notification details");
                navigate('/zentcomm/alert-notification');
            }
        } catch (error) {
            console.error("Error fetching notification details:", error);
            toast.error("Error loading notification");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption ? selectedOption.value : null
        }));
    };

    const handleFile = (file) => {
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData(prev => ({ ...prev, imageSrc: reader.result }));
            };
            reader.readAsDataURL(file);
        } else {
            toast.error('Only image files are allowed!');
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFile(e.target.files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.description || !formData.status) {
            toast.error("Please fill all required fields.");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                userId: 1,
                siteId: 1,
                wingId: 1,
                floorId: 5,
                alertTypeName: formData.title,
                alertDescription: formData.description,
                alertImage: formData.imageSrc,
                createdBy: 'sys',
                isActive: formData.status === 'Active' ? 1 : 0
            };

            let response;
            if (isEdit) {
                response = await apiServices({
                    method: "PUT",
                    url: `/RaisedAlert/UpdateExistingRaisedAlert/${editId}`,
                    data: payload
                });
            } else {
                response = await apiServices({
                    method: "POST",
                    url: `/RaisedAlert/AddNewRaisedAlert`,
                    data: payload
                });
            }

            if (response.isSuccess || response.success || response.IsSuccess) {
                toast.success(`Notification ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentcomm/alert-notification');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} notification`);
            }
        } catch (error) {
            console.error("Submit error:", error);
            toast.error(`An error occurred while ${isEdit ? 'updating' : 'creating'} the notification.`);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center flex-1 h-full bg-white">
                <div className="text-gray-500 font-medium">Loading...</div>
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={isEdit ? "Edit Notification" : "Add New Notification"}
                subHeading="Manage alert notification details"
            />

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Title */}
                    <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                            Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter Alert Title"
                            className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors"
                            required
                        />
                    </div>

                    {/* Status */}
                    <div>
                        <CustomSelect
                            label={<>Status <span className="text-red-500">*</span></>}
                            options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                            value={formData.status ? { label: formData.status, value: formData.status } : null}
                            onChange={(opt) => handleSelectChange('status', opt)}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 mt-6">
                    {/* Description */}
                    <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter Alert Description"
                            className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors min-h-[120px]"
                            required
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 mt-6">
                    {/* Image Upload */}
                    <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">
                            Alert Image
                        </label>
                        <div
                            className="w-full border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {formData.imageSrc ? (
                                <div className="flex flex-col items-center gap-4">
                                    <img src={formData.imageSrc} alt="Preview" className="max-h-48 object-contain rounded-md shadow-sm" />
                                    <p className="text-sm text-gray-500">Click or drag to replace image</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-500">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium">Drag and drop an image here, or click to upload</p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                                ref={fileInputRef}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-between mt-auto border-t pt-4">
                    <button
                        type="button"
                        onClick={() => navigate('/zentcomm/alert-notification')}
                        className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = primaryColor();
                            e.currentTarget.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = secondaryColor();
                            e.currentTarget.style.color = primaryColor();
                        }}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update Notification' : 'Save Notification')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AlertNotificationForm;
